import { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Globe, Newspaper, Plane, Ship, Search, AlertCircle, BarChart3, Bookmark, Settings as SettingsIcon, LogOut, Shield, FileText, Satellite } from 'lucide-react';
import GlobeView from './GlobeView';
import NewsFeed from './NewsFeed';
import ArticleDetail from './ArticleDetail';
import CountryAnalysis from './CountryAnalysis';
import AlertsManager from './AlertsManager';
import Analytics from './Analytics';
import BookmarksView from './BookmarksView';
import SettingsPage from './Settings';
import VesselsView from './VesselsView';
import ReportGenerator from './ReportGenerator';
import SatelliteView from './SatelliteView';

export default function Dashboard({ onLogout, token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const menuItems = [
    { icon: Globe, label: 'Globe', path: '', testId: 'nav-globe' },
    { icon: Newspaper, label: 'News Feed', path: 'news', testId: 'nav-news' },
    { icon: FileText, label: 'Reports', path: 'reports', testId: 'nav-reports' },
    { icon: Plane, label: 'Aircraft', path: 'aircraft', testId: 'nav-aircraft' },
    { icon: Ship, label: 'Vessels', path: 'vessels', testId: 'nav-vessels' },
    { icon: Satellite, label: 'Satellite', path: 'satellite', testId: 'nav-satellite' },
    { icon: Search, label: 'OSINT', path: 'osint', testId: 'nav-osint' },
    { icon: AlertCircle, label: 'Alerts', path: 'alerts', testId: 'nav-alerts' },
    { icon: BarChart3, label: 'Analytics', path: 'analytics', testId: 'nav-analytics' },
    { icon: Bookmark, label: 'Bookmarks', path: 'bookmarks', testId: 'nav-bookmarks' },
  ];

  return (
    <div className="flex h-screen bg-[#050505] overflow-hidden">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-[#0A0A0A] border-r border-white/10 flex flex-col transition-all duration-300`}>
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#00FF9D]" strokeWidth={1.5} />
            {sidebarOpen && (
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed' }}>DEFENSEWATCH</h2>
                <p className="text-[8px] text-zinc-500 uppercase tracking-widest font-mono">OSINT Platform</p>
              </div>
            )}
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const currentPath = location.pathname === '/' ? '' : location.pathname.substring(1);
            const isActive = currentPath === item.path;
            return (
              <button
                key={item.path || 'home'}
                data-testid={item.testId}
                onClick={() => navigate(item.path ? `/${item.path}` : '/')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${
                  isActive
                    ? 'bg-[#00FF9D]/10 text-[#00FF9D] border-l-2 border-[#00FF9D]'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                {sidebarOpen && <span className="font-medium uppercase text-xs tracking-wider">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 space-y-2">
          <button
            data-testid="nav-settings"
            onClick={() => navigate('/settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${
              location.pathname === '/settings'
                ? 'bg-[#00FF9D]/10 text-[#00FF9D] border-l-2 border-[#00FF9D]'
                : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <SettingsIcon className="w-5 h-5" strokeWidth={1.5} />
            {sidebarOpen && <span className="font-medium uppercase text-xs tracking-wider">Settings</span>}
          </button>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#FF3B30] hover:bg-[#FF3B30]/10 text-sm transition-all"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            {sidebarOpen && <span className="font-medium uppercase text-xs tracking-wider">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <Routes>
          <Route index element={<GlobeView />} />
          <Route path="news" element={<NewsFeed />} />
          <Route path="article/:articleId" element={<ArticleDetail />} />
          <Route path="reports" element={<ReportGenerator />} />
          <Route path="aircraft" element={<GlobeView aircraftOnly={true} />} />
          <Route path="vessels" element={<VesselsView />} />
          <Route path="satellite" element={<SatelliteView />} />
          <Route path="osint" element={<CountryAnalysis />} />
          <Route path="alerts" element={<AlertsManager />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="bookmarks" element={<BookmarksView />} />
          <Route path="settings" element={<SettingsPage />} />
        </Routes>
      </div>
    </div>
  );
}